import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { SchemaSalesDemoService } from '@frontend/schema';
import { useAppScopeCatalog } from '@frontend/scope';
import { Chip } from '@frontend/design-system';
import { useLocationPickerDemoUiEnabled } from '../demo-ui-config';

type SandboxResponse = Awaited<ReturnType<typeof SchemaSalesDemoService.GetSandbox>>;

const sandboxRequestCache = new Map<string, Promise<SandboxResponse>>();

const getSandbox = (sandboxId: string) => {
  const cachedRequest = sandboxRequestCache.get(sandboxId);
  if (cachedRequest) {
    return cachedRequest;
  }

  const request = SchemaSalesDemoService.GetSandbox(
    {
      sandboxId,
    },
    { baseUrl: appConfig.BACKEND_API_V2 }
  );

  sandboxRequestCache.set(sandboxId, request);

  return request;
};

export const DemoExpirationChip = ({ orgId }: { orgId: string }) => {
  const { t } = useTranslation('location-picker');
  const { accessibleOrgData } = useAppScopeCatalog();
  const showDemoUi = useLocationPickerDemoUiEnabled();
  const [data, setData] = useState<SandboxResponse | null>(null);

  const org = accessibleOrgData[orgId ?? ''] ?? { sandboxId: undefined, organizationName: undefined };

  useEffect(() => {
    if (!showDemoUi || !org.sandboxId) {
      setData(null);
      return;
    }

    let isSubscribed = true;

    void getSandbox(org.sandboxId)
      .then((sandbox) => {
        if (isSubscribed) {
          setData(sandbox);
        }
      })
      .catch(() => {
        if (isSubscribed) {
          setData(null);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [showDemoUi, org.sandboxId]);

  if (!showDemoUi || !data) {
    return null;
  }

  return (
    <Chip variant='eggplant' size='tiny'>
      {t('{{count}} days left', { count: dayjs(data.expireTime).diff(dayjs(), 'day') ?? 0 })}
    </Chip>
  );
};
